import {Form, Input, Space, Typography} from "@ui";
import {useTranslation} from "react-i18next";
import {PrimaryButton} from "@/components/Button";
import useLogin from "../../hooks/useLogin";
import {useAuthStore} from "../../store/useAuth.store";
import {useLogo} from "../../util/logoUtils";
import {useLoginEmailValidation, useLoginErrorMessage, useLoginFormSubmit, useLoginImages} from "./hooks";
import styles from "./login-form.styles.module.scss";

export const LoginForm = () => {
  const {isPending, isError, error} = useLogin();
  const {validateEmail} = useLoginEmailValidation();
  const {onFinish, onFinishFailed} = useLoginFormSubmit();
  const {getErrorMessage} = useLoginErrorMessage(error);
  const {loginImage} = useLoginImages();

  const logo = useLogo();
  const {t} = useTranslation();
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);

  return (
    <div className={styles.login_wrapper}>
      <div className={styles.login_left}>
        <Form
          name="basic"
          className={styles.form_container_login}
          initialValues={{remember: true}}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          layout="vertical"
        >
          <img
            src={logo}
            alt="logo"
            className={styles.logo_login}
          />
          <Form.Item className={styles.form_container_login_form}>
            {/* <BaseInput
                  id="username"
                  name="username"
                  className={"input"}
                  label="Username"
                /> */}

            <h3>{t("loginSessionTitle")}</h3>
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: t("loginEmail"),
                },
                {
                  validator: (_, value) => {
                    if (value) {
                      const trimmedValue = value.trim();
                      const result = validateEmail(trimmedValue);
                      if (!result.valid) {
                        return Promise.reject(new Error(result.message));
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              style={{width: "100%"}}
            >
              <Input
                placeholder={t("loginEmailPlaceholder")}
                className={styles.Input}
                style={{paddingLeft: "16px"}}
                prefix={
                  <span className={styles.inputIcon}>
                    <svg
                      viewBox="0 0 20 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M18 0H2C0.9 0 0.01 0.9 0.01 2L0 14C0 15.1 0.9 16 2 16H18C19.1 16 20 15.1 20 14V2C20 0.9 19.1 0 18 0ZM18 4L10 9L2 4V2L10 7L18 2V4Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                }
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: t("loginPassword"),
                },
              ]}
            >
              <Input.Password
                placeholder={t("loginPasswordPlaceholder")}
                className={styles.Input}
                style={{paddingLeft: "16px"}}
                prefix={
                  <span className={styles.inputIcon}>
                    <svg
                      viewBox="0 0 16 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M14 7H13V5C13 2.24 10.76 0 8 0C5.24 0 3 2.24 3 5V7H2C0.9 7 0 7.9 0 9V19C0 20.1 0.9 21 2 21H14C15.1 21 16 20.1 16 19V9C16 7.9 15.1 7 14 7ZM8 16C6.9 16 6 15.1 6 14C6 12.9 6.9 12 8 12C9.1 12 10 12.9 10 14C10 15.1 9.1 16 8 16ZM11 7H5V5C5 3.34 6.34 2 8 2C9.66 2 11 3.34 11 5V7Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                }
                iconRender={visible => (
                  <span className={styles.eyeIcon}>
                    {visible ? (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        width="20"
                        height="20"
                      >
                        <path
                          d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5ZM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5Zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3Z"
                          fill="currentColor"
                        />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        width="20"
                        height="20"
                      >
                        <path
                          d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C11.74 7.13 12.35 7 12 7ZM2 4.27l2.28 2.28.46.46A11.8 11.8 0 0 0 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27ZM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2Zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01Z"
                          fill="currentColor"
                        />
                      </svg>
                    )}
                  </span>
                )}
              />
            </Form.Item>
            {/* Show error message if isError is true */}
            {isError && (
              <Form.Item style={{textAlign: "center", color: "var(--accent-danger)"}}>
                <Typography.Text style={{color: "var(--accent-danger)"}}>{getErrorMessage()}</Typography.Text>
              </Form.Item>
            )}
            <Form.Item style={{textAlign: "center", width: "100%"}}>
              <PrimaryButton
                htmlType="submit"
                loading={isPending}
                className={styles.login_submit_button}
              >
                {t("loginSendBtn")}
              </PrimaryButton>
            </Form.Item>
            <Form.Item style={{textAlign: "center"}}>
              <Space
                size="middle"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography.Text
                  onClick={() => handleAuthForm("recover")}
                  className={`${styles.hover_login} ${styles.hoverLogin}`}
                >
                  {t("loginForgotPassword")}
                </Typography.Text>
                <Typography.Text
                  className={styles.hover_login}
                  style={{cursor: "default"}}
                >
                  {t("loginNoAccount")}
                  <Typography.Link
                    className={styles.typography_link}
                    style={{color: "var(--action-primary)"}}
                    onClick={() => handleAuthForm("registration")}
                  >
                    {t("loginRegister")}
                  </Typography.Link>
                </Typography.Text>
              </Space>
            </Form.Item>
          </Form.Item>
        </Form>
      </div>
      <div className={styles.login_right}>
        <img
          src={loginImage}
          alt="logo"
          className={styles.form_container_login_image}
        />
      </div>
    </div>
  );
};
